import { useMemo } from 'react';

import { useSearchCredentials } from '../../entities/search';

/**
 * Palette availability — the app-wide search credentials alone decide it; no Algolia fallback.
 */
export interface PaletteKeys {
  /** General-domain scoped key (every collection but `orders`), or `null`. */
  key: string | null;
  /** Orders-domain scoped key, or `null` when the member may search no order type. */
  ordersKey: string | null;
  /** Typesense browser node URL(s) the keys are valid against. */
  endpoint: string | null;
  /** Collection aliases the keys may search. */
  collections: string[];
  /** True while the first credential fetch for the current (user, org) is in flight. */
  loading: boolean;
}

export interface GlobalSearchAvailability {
  /** True when the palette can drive live search (Typesense keys are available). */
  enabled: boolean;
  token: PaletteKeys;
}

export function useGlobalSearchEnabled(): GlobalSearchAvailability {
  const { credentials, state } = useSearchCredentials();

  return useMemo(() => {
    const loading = state === 'loading';
    const typesense = credentials?.engine === 'typesense' ? credentials : null;
    const token: PaletteKeys = {
      key: typesense?.keys.general || null,
      ordersKey: typesense?.keys.orders || null,
      endpoint: typesense?.endpoint || null,
      collections: [...(typesense?.collections ?? [])],
      loading,
    };
    return { enabled: !loading && token.endpoint !== null && (token.key !== null || token.ordersKey !== null), token };
  }, [credentials, state]);
}
